import React, { useState, useEffect, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/layout/Header";
import BottomNavBar from "../components/layout/BottomNav";
import { useSubscription } from "../providers/SubscriptionProvider";
import ComingSoon from "../components/ComingSoon";

import {
  XMarkIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import { useAuth } from "../contexts/AuthContext";
import { activityService } from "../services/activityService";
import { sessionService } from "../services/sessionService";
import { GlobalActivityService } from "../services/globalActivityService";
import { getUserPublicData } from "../services/userService";
import {
  ActivityType,
  ActivityVariableDefinition,
  TrainingType,
  Sport,
  GlobalActivityType,
} from "../types/activity";
import { trainingTypeService } from "../services/trainingTypeService";
import {
  SessionTemplate,
  Session,
  ActivityMeasurement,
  PERIODISATION_OPTIONS,
} from "../types/post";
import ManageSessionModal from "../components/sessions/ManageSessionModal";
import { PlanSessionModal, StartSessionModal } from "../components/sessions";
import { getDefaultUnit } from "../utils/unitUtils";
import { asDisplayNameOrNull } from "../utils/displayName";
import PeakTextField from "../components/onboarding/PeakTextField";
import {
  LIST_ROW_CLASS,
  ListAddButton,
  ListFilterToggle,
  ListPagination,
  PeakSelect,
} from "../components/common/ListControls";
import SessionActivitiesTable from "../components/sessions/SessionActivitiesTable";

const Sessions = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isPlusOrHigher } = useSubscription();
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const { currentUser, userData, authState } = useAuth();
  const isImperial = authState.user?.isImperial ?? false;
  const [sessionTemplates, setSessionTemplates] = useState<
    (SessionTemplate & { id: string })[]
  >([]);
  const [scheduledSessions, setScheduledSessions] = useState<
    (Session & { id: string })[]
  >([]);
  const [scheduledProposedSessions, setScheduledProposedSessions] = useState<
    (Session & { id: string })[]
  >([]);
  const [activities, setActivities] = useState<ActivityType[]>([]);
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);

  const [showMasterSessionModal, setShowMasterSessionModal] = useState(false);
  const [selectedSessionTemplate, setSelectedSessionTemplate] = useState<
    (SessionTemplate & { id: string }) | null
  >(null);
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  // Modal states for Start and Plan Session
  const [isPlanSessionModalOpen, setIsPlanSessionModalOpen] = useState(false);
  const [isStartSessionModalOpen, setIsStartSessionModalOpen] = useState(false);
  const [startSessionModalMode, setStartSessionModalMode] = useState<
    "start" | "completed"
  >("start");
  const [selectedTemplate, setSelectedTemplate] = useState<
    (SessionTemplate & { id: string }) | null
  >(null);

  // Floating menu state
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);

  // Search states for main lists
  const [sessionsSearchTerm, setSessionsSearchTerm] = useState<string>("");

  // Filter states
  const [sportFilter, setSportFilter] = useState<string>("all");
  const [sessionFilter, setSessionFilter] = useState<string>("all");
  const [periodisationFilter, setPeriodisationFilter] = useState<string>("all");
  const [showSessionFilters, setShowSessionFilters] = useState(false);

  const [availableSports, setAvailableSports] = useState<Sport[]>([]); // user's sports (for the filter dropdown)

  // Pagination states
  const [sessionsPage, setSessionsPage] = useState(1);
  const SESSIONS_PER_PAGE = 10;

  // Match mobile: planner/start are free; only saved session templates need Plus.
  const canCreateSession = isPlusOrHigher;

  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(false);

  // Global Activity Types state (for template name lookups)
  const [globalActivityTypes, setGlobalActivityTypes] = useState<
    Record<string, GlobalActivityType[]>
  >({});
  const [packActivityTypes, setPackActivityTypes] = useState<ActivityType[]>(
    [],
  );
  const [userAddedPackIds, setUserAddedPackIds] = useState<string[]>([]);

  // Quick start state
  const [isQuickStarting, setIsQuickStarting] = useState<boolean>(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState<boolean>(false);
  const [copySessionLoading, setCopySessionLoading] = useState<boolean>(false);
  const [copySessionError, setCopySessionError] = useState<string | null>(null);
  const [copySourceSession, setCopySourceSession] = useState<Session | null>(
    null,
  );
  const [copySourceActivities, setCopySourceActivities] = useState<
    ActivityType[]
  >([]);
  const [copyStep, setCopyStep] = useState<
    "idle" | "creating_activities" | "creating_template" | "done" | "error"
  >("idle");
  const [copyActionMessage, setCopyActionMessage] = useState<string>("");
  const [copyInProgress, setCopyInProgress] = useState<boolean>(false);
  const [copyCreatedTemplateName, setCopyCreatedTemplateName] =
    useState<string>("");
  const [copySourceUsername, setCopySourceUsername] = useState<string>("");

  // Template delete state
  const [templateToDelete, setTemplateToDelete] = useState<
    (SessionTemplate & { id: string }) | null
  >(null);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState<boolean>(false);

  const [loadingActivities, setLoadingActivities] = useState<boolean>(false);

  const [featureKeyForModal, setFeatureKeyForModal] = useState<string>("");

  // Combined list of user activities + pack activities for name lookups
  const allActivities = React.useMemo(() => {
    const userNames = new Set(activities.map((a) => a.name.toLowerCase()));
    const uniquePackActivities = packActivityTypes.filter(
      (pa) => !userNames.has(pa.name.toLowerCase()),
    );
    return [...activities, ...uniquePackActivities];
  }, [activities, packActivityTypes]);

  const getRawActivityId = (activityTypeId: string): string => {
    if (activityTypeId.startsWith("global_")) {
      const parts = activityTypeId.split("_");
      if (parts.length >= 3) {
        return parts.slice(2).join("_");
      }
    }
    return activityTypeId;
  };

  const getGlobalCategoryIdFromActivityTypeId = (
    activityTypeId: string,
  ): string | undefined => {
    if (!activityTypeId.startsWith("global_")) return undefined;
    const parts = activityTypeId.split("_");
    if (parts.length >= 3) {
      return parts[1];
    }
    return undefined;
  };

  const getGlobalActivityName = (
    measurement: ActivityMeasurement,
  ): string | null => {
    const rawActivityId = getRawActivityId(measurement.ActivityTypeID);
    const categoryId =
      measurement.globalCategoryID ||
      getGlobalCategoryIdFromActivityTypeId(measurement.ActivityTypeID);

    if (categoryId) {
      const categoryTypes = globalActivityTypes[categoryId] || [];
      const match = categoryTypes.find((type) => type.id === rawActivityId);
      if (match?.name) return match.name;
    }

    for (const categoryTypes of Object.values(globalActivityTypes)) {
      const match = categoryTypes.find((type) => type.id === rawActivityId);
      if (match?.name) return match.name;
    }

    return null;
  };

  // Helper function to get activity names from a template
  const getActivityNamesFromTemplate = (
    template: SessionTemplate & { id: string },
  ): string[] => {
    if (
      !template.ActivityMeasurementList ||
      template.ActivityMeasurementList.length === 0
    ) {
      return [];
    }

    const activityNames = new Set<string>();

    template.ActivityMeasurementList.forEach((measurement) => {
      const rawActivityId = getRawActivityId(measurement.ActivityTypeID);
      const userOrPackMatch = allActivities.find((a) => a.id === rawActivityId);
      if (userOrPackMatch?.name) {
        activityNames.add(userOrPackMatch.name);
        return;
      }

      const globalName = getGlobalActivityName(measurement);
      if (globalName) {
        activityNames.add(globalName);
      }
    });

    return Array.from(activityNames);
  };

  const copyUIDParam = searchParams.get("copyUID");
  const copySessionIDParam = searchParams.get("copySessionID");

  const clearCopyParams = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("copyUID");
    next.delete("copySessionID");
    setSearchParams(next, { replace: true });
  };

  const getCopyPreviewActivityName = (measurement: ActivityMeasurement) => {
    const rawActivityId = getRawActivityId(measurement.ActivityTypeID);
    const sourceMatch = copySourceActivities.find(
      (type) => type.id === rawActivityId,
    );
    if (sourceMatch?.name) return sourceMatch.name;

    const globalName = getGlobalActivityName(measurement);
    if (globalName) {
      return globalName;
    }

    if (
      measurement.globalCategoryID ||
      measurement.ActivityTypeID.startsWith("global_")
    ) {
      return "Global Exercise";
    }
    return "Unknown Exercise";
  };

  useEffect(() => {
    const fetchAllGlobalActivitiesForLookup = async () => {
      if (!currentUser?.uid) return;

      try {
        const categories = await GlobalActivityService.fetchCategories();
        const categoryIds = categories
          .map((category) => category.id)
          .filter((id): id is string => !!id);

        if (categoryIds.length === 0) return;

        const allGlobalTypes =
          await GlobalActivityService.fetchActivityTypesForCategories(
            categoryIds,
          );

        const grouped = allGlobalTypes.reduce<Record<string, ActivityType[]>>(
          (acc, type) => {
            const categoryId = type.categoryId;
            if (!categoryId) return acc;
            if (!acc[categoryId]) acc[categoryId] = [];
            acc[categoryId].push({
              id: type.id,
              name: type.name,
              description: type.description,
              variables: type.variables,
              image: type.image,
              isFromPack: true,
              categoryId: type.categoryId,
            });
            return acc;
          },
          {},
        );

        setGlobalActivityTypes((prev) => {
          const next = { ...prev };
          Object.entries(grouped).forEach(([categoryId, types]) => {
            const existing = next[categoryId] || [];
            const merged = [...existing, ...types];
            next[categoryId] = Array.from(
              new Map(merged.map((type) => [type.id, type])).values(),
            );
          });
          return next;
        });
      } catch (error) {
        console.error("Error preloading global activities for lookup:", error);
      }
    };

    fetchAllGlobalActivitiesForLookup();
  }, [currentUser?.uid]);

  const normalizeMeasurementForTemplate = (
    measurement: ActivityMeasurement,
  ) => ({
    ActivityTypeID: measurement.ActivityTypeID,
    variables: (measurement.variables || []).map((variable) => ({
      ...variable,
    })),
    ...(measurement.globalCategoryID && {
      globalCategoryID: measurement.globalCategoryID,
    }),
  });

  useEffect(() => {
    const loadCopySession = async () => {
      if (!copyUIDParam || !copySessionIDParam || !currentUser?.uid) return;

      try {
        setCopyDialogOpen(true);
        setCopySessionLoading(true);
        setCopySessionError(null);
        setCopySourceSession(null);
        setCopySourceActivities([]);
        setCopyStep("idle");
        setCopyActionMessage("");
        setCopyCreatedTemplateName("");
        setCopySourceUsername("");

        const [sourceSession, sourceActivities, sourceUserData] =
          await Promise.all([
            sessionService.getSession(copyUIDParam, copySessionIDParam),
            activityService.fetchActivityTypes(copyUIDParam).catch(() => []),
            getUserPublicData(copyUIDParam).catch(() => null),
          ]);

        if (!sourceSession) {
          setCopySessionError("Could not load this session.");
          return;
        }

        setCopySourceSession(sourceSession);
        setCopySourceActivities(sourceActivities);
        setCopySourceUsername(sourceUserData?.username || "");

        const categoryIDs = Array.from(
          new Set(
            (sourceSession.ActivityMeasurementList || [])
              .map(
                (m) =>
                  m.globalCategoryID ||
                  getGlobalCategoryIdFromActivityTypeId(m.ActivityTypeID),
              )
              .filter((id): id is string => !!id),
          ),
        );

        const missingCategoryIDs = categoryIDs.filter(
          (categoryID) => !globalActivityTypes[categoryID],
        );

        if (missingCategoryIDs.length > 0) {
          const fetched = await Promise.all(
            missingCategoryIDs.map(async (categoryID) => ({
              categoryID,
              activities:
                await GlobalActivityService.fetchActivityTypes(categoryID),
            })),
          );

          setGlobalActivityTypes((prev) => {
            const next = { ...prev };
            fetched.forEach(({ categoryID, activities }) => {
              next[categoryID] = activities;
            });
            return next;
          });
        }
      } catch (error) {
        console.error("Error loading copy session data:", error);
        setCopySessionError("Failed to load session for copying.");
      } finally {
        setCopySessionLoading(false);
      }
    };

    loadCopySession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyUIDParam, copySessionIDParam, currentUser?.uid]);

  // Fetch activity types, session templates, scheduled sessions, and session types from Firebase
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.uid) return;

      try {
        setLoadingActivities(true);
        setLoadingTemplates(true);

        // Log training sport for debugging

        const [typesData, templatesData, sessionsData, proposedSessionsData] =
          await Promise.all([
            activityService.fetchActivityTypes(currentUser.uid),
            sessionService.getAllSessionTemplates(currentUser.uid),
            sessionService.getAllSessions(currentUser.uid),
            sessionService.getProposedSessions(currentUser.uid),
          ]);

        setActivities(typesData);
        setSessionTemplates(templatesData);
        setScheduledSessions(sessionsData);
        setScheduledProposedSessions(proposedSessionsData);

        // console.log("Fetched activity types:", typesData.length);
        // console.log("Fetched templates:", templatesData.length);
        // console.log("Fetched scheduled sessions:", sessionsData.length);

        // Fetch training types for the user's selected sports
        try {
          const userTrainingTypes =
            await trainingTypeService.fetchTrainingTypesForSports(
              userData?.trainingSportIDs,
              userData?.customTrainingTypes,
            );
          setTrainingTypes(userTrainingTypes);

          // Sport names for the filter dropdown (only useful with multiple sports)
          if (
            userData?.trainingSportIDs &&
            userData.trainingSportIDs.length > 1
          ) {
            const allSports = await trainingTypeService.fetchSports();
            setAvailableSports(
              allSports.filter(
                (s) => s.id && userData.trainingSportIDs!.includes(s.id),
              ),
            );
          }
        } catch (error) {
          console.error("Error fetching training types:", error);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoadingActivities(false);
        setLoadingTemplates(false);
      }
    };

    fetchData();
  }, [currentUser?.uid, userData?.trainingSportIDs, userData?.customTrainingTypes]);

  // Template delete handlers

  const handleDeleteTemplateClick = (
    template: SessionTemplate & { id: string },
  ) => {
    setTemplateToDelete(template);
  };

  const handleDeleteTemplate = async () => {
    if (!currentUser?.uid || !templateToDelete) return;

    try {
      setIsDeletingTemplate(true);
      await sessionService.deleteSessionTemplate(
        currentUser.uid,
        templateToDelete.id,
      );

      // Refresh the templates list
      const templatesData = await sessionService.getAllSessionTemplates(
        currentUser.uid,
      );
      setSessionTemplates(templatesData);

      setTemplateToDelete(null);

      // Close the ManageSessionModal if it's open
      if (showMasterSessionModal) {
        setShowMasterSessionModal(false);
        setSelectedSessionTemplate(null);
      }
    } catch (error) {
      console.error("Error deleting session template:", error);
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  const handleCancelDeleteTemplate = () => {
    setTemplateToDelete(null);
  };

  // Quick start handler - creates a blank session and navigates to it
  const handleQuickStart = async () => {
    if (!currentUser?.uid) return;

    try {
      setIsQuickStarting(true);

      const sessionData = {
        ActivityMeasurementList: [],
        description: "",
        plannedDate: new Date().toISOString().split("T")[0],
        status: "executing" as const,
        startTime: new Date().toISOString(),
        startedAdHoc: true,
      };

      const sessionId = await sessionService.createSession(
        currentUser.uid,
        sessionData,
      );

      navigate(`/session/${sessionId}`);
    } catch (error) {
      console.error("Error quick-starting session:", error);
    } finally {
      setIsQuickStarting(false);
    }
  };

  // Session modal handlers
  const handlePlanSessionClick = () => {
    setIsPlanSessionModalOpen(true);
    setSelectedTemplate(null); // Start from scratch by default
  };

  const handleStartSessionClick = () => {
    setIsStartSessionModalOpen(true);
  };
  const handleWizardAction = React.useCallback(
    (action: "add" | "start") => {
      setShowFloatingMenu(false);

      if (action === "add") {
        if (!canCreateSession) {
          setFeatureKeyForModal("savedSessions");
          setShowPremiumModal(true);
          return;
        }
        setShowMasterSessionModal(true);
      } else if (action === "start") {
        handleStartSessionClick();
      }
    },
    [canCreateSession, handleStartSessionClick],
  );
  const handleStartSessionCreated = (sessionId: string) => {
    setIsStartSessionModalOpen(false);
    // Navigation is handled by StartSessionModal
  };

  // Load user's added pack IDs from userData and fetch pack activity types
  useEffect(() => {
    if (userData?.adminActivityCatIDs) {
      console.log(
        "[Planner] User has added packs:",
        userData.adminActivityCatIDs,
      );
      setUserAddedPackIds(userData.adminActivityCatIDs);

      // Fetch pack activity types so template name lookups work
      const packIds = userData.adminActivityCatIDs;
      const fetchPackActivities = async () => {
        if (packIds.length === 0) {
          // console.log("[Planner] No pack IDs, clearing pack activities");
          setPackActivityTypes([]);
          return;
        }
        try {
          // console.log("[Planner] Fetching pack activities for:", packIds);
          const globalTypes =
            await GlobalActivityService.fetchActivityTypesForCategories(
              packIds,
            );
          // console.log(
          //   "[Planner] Fetched global types:",
          //   globalTypes.length,
          //   "activities",
          // );
          const converted: ActivityType[] = globalTypes.map(
            (gt: GlobalActivityType) => ({
              id: gt.id, // Use raw Firestore ID (templates strip the global_ prefix)
              name: gt.name,
              description: gt.description,
              variables: gt.variables.map((v): ActivityVariableDefinition => {
                if (typeof v === "string") {
                  return {
                    name: v,
                    defaultUnit: getDefaultUnit(v, isImperial),
                  };
                }
                return {
                  ...v,
                  defaultUnit: getDefaultUnit(v.name, isImperial),
                };
              }),
              image: gt.image,
              isFromPack: true,
              categoryId: gt.categoryId,
            }),
          );
          // console.log(
          //   "[Planner] Converted pack activities:",
          //   converted.length,
          //   "activities",
          // );
          setPackActivityTypes(converted);
        } catch (error) {
          console.error("[Planner] Error fetching pack activity types:", error);
        }
      };
      fetchPackActivities();
    } else {
      // console.log("[Planner] No adminActivityCatIDs in userData");
      setUserAddedPackIds([]);
      setPackActivityTypes([]);
    }
  }, [userData?.adminActivityCatIDs, isImperial]);

  // Session template click handler - opens master session modal
  const handleSessionClick = async (
    sessionTemplate: SessionTemplate & { id: string },
  ) => {
    setSelectedSessionTemplate(sessionTemplate);
    setShowMasterSessionModal(true);
  };

  // Master session modal handlers
  const handleMasterSessionModalClose = () => {
    setShowMasterSessionModal(false);
    setSelectedSessionTemplate(null);
  };

  const handleSessionCreated = async (sessionIds: string[] = []) => {
    if (sessionIds.length > 0) {
      console.log("Sessions created with IDs:", sessionIds);
    }
    // Refresh the templates list and scheduled sessions after session creation
    if (currentUser?.uid) {
      try {
        const [templatesData, sessionsData] = await Promise.all([
          sessionService.getAllSessionTemplates(currentUser.uid),
          sessionService.getAllSessions(currentUser.uid),
        ]);
        setSessionTemplates(templatesData);
        setScheduledSessions(sessionsData);
      } catch (error) {
        console.error("Error refreshing data:", error);
      }
    }

    handleMasterSessionModalClose();
    // Clear selected template for PlanSessionModal
    setSelectedTemplate(null);
  };

  const handleAddCopiedSession = async () => {
    if (!currentUser?.uid || !copySourceSession) return;

    const sourceMeasurements = copySourceSession.ActivityMeasurementList || [];
    const isGlobalMeasurement = (measurement: ActivityMeasurement) =>
      !!measurement.globalCategoryID ||
      measurement.ActivityTypeID.startsWith("global_");
    const customActivityIDs = Array.from(
      new Set(
        sourceMeasurements
          .filter((measurement) => !isGlobalMeasurement(measurement))
          .map((measurement) => measurement.ActivityTypeID),
      ),
    );

    const sourceActivitiesById = new Map(
      copySourceActivities.map((activity) => [activity.id, activity]),
    );
    const existingByName = new Map(
      activities.map((activity) => [
        activity.name.trim().toLowerCase(),
        activity,
      ]),
    );
    const mappedActivityIDs: Record<string, string> = {};

    try {
      setCopyInProgress(true);
      setCopyStep("creating_activities");
      setCopyActionMessage("Adding required activities...");

      for (const sourceActivityID of customActivityIDs) {
        if (mappedActivityIDs[sourceActivityID]) continue;

        const sourceActivity = sourceActivitiesById.get(sourceActivityID);
        const sourceName =
          asDisplayNameOrNull(sourceActivity?.name) ||
          `Imported Activity ${sourceActivityID}`;
        const existingActivity = existingByName.get(sourceName.toLowerCase());

        if (existingActivity?.id) {
          mappedActivityIDs[sourceActivityID] = existingActivity.id;
          continue;
        }

        const fallbackVarsFromSession =
          sourceMeasurements.find(
            (measurement) =>
              !measurement.globalCategoryID &&
              measurement.ActivityTypeID === sourceActivityID,
          )?.variables || [];

        const normalizedVariables: ActivityVariableDefinition[] = (
          sourceActivity?.variables || []
        ).length
          ? (sourceActivity!.variables as ActivityVariableDefinition[]).map(
              (variable) => ({
                ...(typeof variable === "string"
                  ? { name: variable, defaultUnit: getDefaultUnit(variable) }
                  : variable),
              }),
            )
          : fallbackVarsFromSession.map((variable) => ({
              name: variable.name,
              defaultUnit: variable.unit || getDefaultUnit(variable.name),
            }));
        const safeVariables =
          normalizedVariables.length > 0
            ? normalizedVariables
            : [{ name: "Reps", defaultUnit: getDefaultUnit("Reps") }];

        const createdActivityID = await activityService.createActivityType(
          currentUser.uid,
          {
            name: sourceName,
            description: sourceActivity?.description || "",
            variables: safeVariables,
            image: sourceActivity?.image || "",
          },
        );

        mappedActivityIDs[sourceActivityID] = createdActivityID;
        existingByName.set(sourceName.toLowerCase(), {
          ...(sourceActivity || {
            name: sourceName,
            description: "",
            variables: normalizedVariables,
          }),
          id: createdActivityID,
        } as ActivityType);
      }

      setCopyStep("creating_template");
      setCopyActionMessage("Creating session template...");

      const cleanedMeasurements: ActivityMeasurement[] = sourceMeasurements.map(
        (measurement) => {
          if (isGlobalMeasurement(measurement)) {
            return normalizeMeasurementForTemplate(measurement);
          }

          const mappedActivityID =
            mappedActivityIDs[measurement.ActivityTypeID];
          if (!mappedActivityID) {
            throw new Error(
              `Missing mapped activity for source activity ${measurement.ActivityTypeID}`,
            );
          }

          return normalizeMeasurementForTemplate({
            ...measurement,
            ActivityTypeID: mappedActivityID,
          });
        },
      );

      const templateName =
        asDisplayNameOrNull(copySourceSession.name) ||
        `Copied Session Template from ${copySourceUsername || "Unknown User"}`;

      await sessionService.createSessionTemplate(currentUser.uid, {
        name: templateName,
        description: copySourceSession.description || "",
        ActivityMeasurementList: cleanedMeasurements,
      });

      const [templatesData, refreshedActivities] = await Promise.all([
        sessionService.getAllSessionTemplates(currentUser.uid),
        activityService.fetchActivityTypes(currentUser.uid),
      ]);

      setSessionTemplates(templatesData);
      setActivities(refreshedActivities);
      setCopyCreatedTemplateName(templateName);
      setCopyStep("done");
      setCopyActionMessage("Done");
      clearCopyParams();

      // Auto-close after a short delay so user sees the session in the list
      setTimeout(() => {
        setCopyDialogOpen(false);
      }, 800);
    } catch (error) {
      console.error("Error adding copied session:", error);
      setCopyStep("error");
      setCopyActionMessage("Failed to add copied session");
      setCopySessionError("Failed to add this session. Please try again.");
    } finally {
      setCopyInProgress(false);
    }
  };

  // Pagination helper
  // Shared template filter used by both the list and the pagination count.
  // Sport/type matching resolves each template's training type (new
  // trainingTypeID or legacy composite sessionTypeID) against the
  // trainingTypes collection.
  const templateMatchesFilters = (
    template: SessionTemplate & { id: string },
  ): boolean => {
    const resolvedType = trainingTypeService.resolveTrainingTypeForSession(
      template,
      trainingTypes,
    );

    // Sport filter: keep templates whose training type belongs to the
    // selected sport; templates with no resolvable type stay visible.
    if (sportFilter !== "all") {
      const sport = availableSports.find((s) => s.id === sportFilter);
      if (
        resolvedType?.id &&
        sport &&
        !sport.trainingTypeIDs?.includes(resolvedType.id)
      ) {
        return false;
      }
    }

    // Training type filter (value is a trainingTypes doc ID).
    if (sessionFilter !== "all" && resolvedType?.id !== sessionFilter) {
      return false;
    }

    // Periodisation filter
    if (
      periodisationFilter !== "all" &&
      template.periodisation !== periodisationFilter
    ) {
      return false;
    }

    // Search filter
    if (!sessionsSearchTerm) return true;
    const searchLower = sessionsSearchTerm.toLowerCase();
    return (
      (template.name || "").toLowerCase().includes(searchLower) ||
      (template.description || "").toLowerCase().includes(searchLower) ||
      getActivityNamesFromTemplate(template).some((activityName) =>
        activityName.toLowerCase().includes(searchLower),
      )
    );
  };

  const paginateArray = <T,>(
    array: T[],
    page: number,
    itemsPerPage: number,
  ) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return {
      items: array.slice(startIndex, endIndex),
      totalPages: Math.ceil(array.length / itemsPerPage),
      currentPage: page,
      totalItems: array.length,
    };
  };



  return (
    <div className="w-full min-h-screen">
      {/* Header */}
      <Header />
      {showPremiumModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start backdrop-blur-sm justify-center pt-20 pb-32 p-4">
          <div className="relative">
            <button
              onClick={() => setShowPremiumModal(false)}
              className="absolute -top-3 -right-3 bg-base-100 rounded-full px-2 py-1 text-sm"
            >
              ✕
            </button>

            <ComingSoon featureKey={featureKeyForModal || "savedSessions"} />
          </div>
        </div>
      )}
      {/* Main Content */}
      <div className="pt-16 pb-32">
        <div className="max-w-5xl mx-auto">
          <div className="flex justify-between items-center mb-6 pt-8 px-8 sm:px-0">
            <div>
              <div className="flex items-center">
                <h1 className="text-3xl sm:text-3xl md:text-4xl font-bold text-base-content font-heading">
                  Sessions
                </h1>
                <button
                  onClick={() => setShowHelpDialog(true)}
                  className="p-1 text-base-content/60 hover:text-base-content/80 transition-colors pl-4"
                  aria-label="Help"
                >
                  <QuestionMarkCircleIcon className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="relative">
              <ListAddButton
                label="Add session"
                onClick={() => setShowFloatingMenu(!showFloatingMenu)}
              />

              {/* Dropdown Menu */}
              {showFloatingMenu && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowFloatingMenu(false)}
                  />

                  {/* Menu */}
                  <div className="absolute top-full right-0 mt-2 z-50">
                    <div className="bg-base-100 rounded-lg shadow-xl border border-base-300 overflow-hidden min-w-[180px]">
                      {/* Session Option */}
                      <button
                        onClick={() => handleWizardAction("add")}
                        className="w-full px-4 py-3 text-left font-grotesk font-medium text-base-content hover:bg-base-200 transition-colors border-b border-base-300"
                      >
                        <strong className="font-black">Add</strong> Session
                      </button>

                      {/* Start Session Option */}
                      <button
                        onClick={() => {
                          setStartSessionModalMode("start");
                          handleWizardAction("start");
                        }}
                        className="w-full px-4 py-3 text-left font-grotesk font-medium text-base-content hover:bg-base-200 transition-colors border-b border-base-300"
                      >
                        <strong className="font-black">Start</strong> Session
                      </button>

                      {/* Add Completed Session Option */}
                      <button
                        onClick={() => {
                          setShowFloatingMenu(false);
                          setStartSessionModalMode("completed");
                          setIsStartSessionModalOpen(true);
                        }}
                        className="w-full px-4 py-3 text-left font-grotesk font-medium text-base-content hover:bg-base-200 transition-colors border-b border-base-300"
                      >
                        <strong className="font-black">Add</strong> Completed
                        Session
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Action Buttons */}
          {/* <div className="px-4 mb-6">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <button
                onClick={handleStartSessionClick}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-neutral rounded-lg hover:bg-neutral/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
              >
                <HiPlay className="text-base" />
                Start Session
              </button>
            </div>
          </div> */}

          {/* Session Templates */}
          <div className="space-y-4">
            {/* Sessions Search Bar and Filter */}
            <div className="px-8 sm:px-0 space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <PeakTextField
                    label="Search"
                    value={sessionsSearchTerm}
                    onChange={setSessionsSearchTerm}
                    placeholder="Search sessions…"
                    autoCapitalize="none"
                  />
                </div>
                <ListFilterToggle
                  open={showSessionFilters}
                  active={
                    showSessionFilters ||
                    sportFilter !== "all" ||
                    sessionFilter !== "all" ||
                    periodisationFilter !== "all"
                  }
                  onToggle={() => setShowSessionFilters((prev) => !prev)}
                />
              </div>

              {showSessionFilters && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {availableSports.length > 1 && (
                    <PeakSelect
                      label="Sport"
                      value={sportFilter}
                      onChange={(value) => {
                        setSportFilter(value);
                        setSessionFilter("all");
                      }}
                      emphasizedValue
                      options={[
                        { value: "all", label: "All Sports" },
                        ...availableSports
                          .map((sport) => ({
                            value: sport.id || "",
                            label: sport.name,
                          }))
                          .sort((a, b) => a.label.localeCompare(b.label)),
                      ]}
                    />
                  )}
                  <PeakSelect
                    label="Session Type"
                    value={sessionFilter}
                    onChange={setSessionFilter}
                    emphasizedValue
                    options={[
                      { value: "all", label: "All Session Types" },
                      ...trainingTypes
                        .filter((type) => {
                          if (availableSports.length > 1 && sportFilter !== "all") {
                            const sport = availableSports.find(
                              (s) => s.id === sportFilter,
                            );
                            return (
                              !!type.id &&
                              !!sport?.trainingTypeIDs?.includes(type.id)
                            );
                          }
                          return true;
                        })
                        .map((type) => ({
                          value: type.id || "",
                          label: type.name,
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label)),
                    ]}
                  />
                  <PeakSelect
                    label="Periodisation"
                    value={periodisationFilter}
                    onChange={setPeriodisationFilter}
                    emphasizedValue
                    options={[
                      { value: "all", label: "All Periodisation" },
                      ...PERIODISATION_OPTIONS.map((option) => ({
                        value: option.value,
                        label: option.label,
                      })),
                    ]}
                  />
                </div>
              )}
            </div>

            {loadingTemplates ? (
              <div className="py-16 text-center font-funnel text-sm text-peak-text-tertiary">
                Loading session templates...
              </div>
            ) : sessionTemplates.length === 0 ? (
              <div className="text-center pt-20">
                <h3 className="text-sm font-medium text-base-content/60 mb-4 font-heading">
                  No session templates created yet.
                </h3>
                {/* Add Session Button */}
                <div className="flex justify-center">
                  <button
                    onClick={() => {
                      if (!canCreateSession) {
                        setFeatureKeyForModal("savedSessions");
                        setShowPremiumModal(true);
                        return;
                      }
                      setShowMasterSessionModal(true);
                    }}
                  >
                    Add Session
                  </button>
                </div>
              </div>
            ) : (
              (() => {
                const getDisplayTitle = (
                  t: (typeof sessionTemplates)[number],
                ) => {
                  const safeName = asDisplayNameOrNull(t.name);
                  if (safeName) return safeName;
                  const stName = trainingTypeService.getSessionTrainingTypeName(
                    t,
                    trainingTypes,
                  );
                  if (stName) return stName;
                  const actNames = getActivityNamesFromTemplate(t);
                  return actNames.length > 0 ? actNames[0] : "Session";
                };
                const filteredTemplates = sessionTemplates
                  .filter(templateMatchesFilters)
                  .sort((a, b) =>
                    getDisplayTitle(a)
                      .toLowerCase()
                      .localeCompare(getDisplayTitle(b).toLowerCase()),
                  );
                const paginatedData = paginateArray(
                  filteredTemplates,
                  sessionsPage,
                  SESSIONS_PER_PAGE,
                );

                if (filteredTemplates.length === 0) {
                  return (
                    <div className="mx-8 sm:mx-0 py-16 text-center font-funnel text-sm text-peak-text-tertiary">
                      No sessions match your search.
                    </div>
                  );
                }

                return (
                  <>
                    <div className="mx-8 sm:mx-0 border-t border-peak-border">
                      {paginatedData.items.map((template) => {
                        const sessionTypeName =
                          trainingTypeService.getSessionTrainingTypeName(
                            template,
                            trainingTypes,
                          );
                        const periodisationLabel = template.periodisation
                          ? PERIODISATION_OPTIONS.find(
                              (p) => p.value === template.periodisation,
                            )?.label
                          : null;
                        const meta = [sessionTypeName, periodisationLabel].filter(
                          Boolean,
                        );
                        const count = template.ActivityMeasurementList?.length ?? 0;

                        return (
                          <button
                            key={template.id}
                            type="button"
                            onClick={() => handleSessionClick(template)}
                            className={`${LIST_ROW_CLASS} block w-full text-left transition-colors hover:bg-peak-bg-secondary/50`}
                          >
                            <div className="flex items-baseline justify-between gap-3">
                              <span className="min-w-0 font-funnel text-[15px] font-semibold text-peak-text">
                                {getDisplayTitle(template)}
                              </span>
                              <span className="flex-shrink-0 whitespace-nowrap">
                                <span className="font-geist text-[15px] font-semibold tabular-nums text-peak-text">
                                  {count}
                                </span>
                                <span className="ml-1 font-funnel text-xs text-peak-text-tertiary">
                                  {count === 1 ? "exercise" : "exercises"}
                                </span>
                              </span>
                            </div>
                            {meta.length > 0 && (
                              <p className="mt-1 truncate font-funnel text-xs font-medium text-peak-text-tertiary">
                                {meta.join("  ·  ")}
                              </p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mx-8 sm:mx-0">
                      <ListPagination
                        currentPage={sessionsPage}
                        totalPages={paginatedData.totalPages}
                        onPageChange={setSessionsPage}
                      />
                    </div>
                  </>
                );
              })()
            )}
          </div>
          {/* Completed Sessions Link */}
          {scheduledSessions.some((s) => s.status === "complete") && (
            <div className="mt-10 px-8 sm:px-0">
              <button
                onClick={() => navigate("/sessions/completed")}
                className="flex items-center gap-2 w-full group"
              >
                <CheckCircleIcon className="w-5 h-5 text-base-content/50" />
                <span className="text-sm font-semibold text-base-content/70 font-heading group-hover:text-base-content transition-colors">
                  Completed (
                  {
                    scheduledSessions.filter((s) => s.status === "complete")
                      .length
                  }
                  )
                </span>
                <ChevronDownIcon className="w-4 h-4 text-base-content/50 -rotate-90" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Manage Session Modal */}
      {showMasterSessionModal && (
        <ManageSessionModal
          isOpen={showMasterSessionModal}
          onClose={handleMasterSessionModalClose}
          onSessionCreated={handleSessionCreated}
          existingSession={selectedSessionTemplate}
          onDelete={(session) => {
            setTemplateToDelete(session as SessionTemplate & { id: string });
          }}
          onCreateNew={() => setSelectedSessionTemplate(null)}
        />
      )}

      {/* Session Template Delete Confirmation Dialog */}
      {templateToDelete && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10001] p-4"
          onClick={handleCancelDeleteTemplate}
        >
          <div
            className="bg-base-100 rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-base-content mb-4 font-heading">
              Delete Session Template
            </h3>
            <p className="text-base-content/70 mb-6 font-grotesk">
              Are you sure you want to delete the session template{" "}
              <strong>"{templateToDelete.name}"</strong>? This will permanently
              delete the template, but activities in the template will remain
              available.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleCancelDeleteTemplate}
                disabled={isDeletingTemplate}
                className="px-4 py-3 text-sm font-medium text-base-content/80 bg-base-100 border border-base-300 rounded-lg hover:bg-base-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteTemplate}
                disabled={isDeletingTemplate}
                className="px-4 py-3 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
              >
                {isDeletingTemplate ? (
                  <>
                    <span className="loading loading-spinner loading-sm mr-2"></span>
                    Deleting...
                  </>
                ) : (
                  "Delete Template"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Session Modal */}
      <PlanSessionModal
        isOpen={isPlanSessionModalOpen}
        onClose={() => {
          setIsPlanSessionModalOpen(false);
          setSelectedTemplate(null);
        }}
        onSessionCreated={handleSessionCreated}
        initialTemplate={selectedTemplate}
      />

      {/* Start / Add Completed Session Modal */}
      <StartSessionModal
        isOpen={isStartSessionModalOpen}
        onClose={() => setIsStartSessionModalOpen(false)}
        onSessionCreated={handleStartSessionCreated}
        mode={startSessionModalMode}
      />

      {/* Copy Session Dialog */}
      <Transition appear show={copyDialogOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            if (copyInProgress) return;
            setCopyDialogOpen(false);
            clearCopyParams();
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 z-[60] overflow-y-auto p-0 md:p-4">
            <div className="flex min-h-full items-end md:items-center justify-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-full md:translate-y-0 md:scale-95"
                enterTo="opacity-100 translate-y-0 md:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 md:scale-100"
                leaveTo="opacity-0 translate-y-full md:translate-y-0 md:scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl h-screen md:h-auto md:rounded-xl bg-base-100 border border-base-300 shadow-xl flex flex-col">
                  <div className="px-8 py-8 pb-0 flex items-center justify-between">
                    <Dialog.Title className="text-lg font-semibold text-base-content font-heading">
                      Copy Session
                    </Dialog.Title>
                    <button
                      onClick={() => {
                        if (copyInProgress) return;
                        setCopyDialogOpen(false);
                        clearCopyParams();
                      }}
                      className="text-base-content/60 hover:text-base-content transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="p-8 pt-0 space-y-4 flex-1 overflow-y-auto">
                    {copySessionLoading ? (
                      <div className="text-sm text-base-content/70 font-grotesk">
                        Loading session...
                      </div>
                    ) : copySessionError ? (
                      <div className="text-sm text-red-600 font-grotesk">
                        {copySessionError}
                      </div>
                    ) : copySourceSession ? (
                      <>
                        <div className="space-y-1">
                          <div className="font-light text-sm text-base-content font-heading">
                            {asDisplayNameOrNull(copySourceSession.name) ||
                              "Session Details"}
                          </div>
                          {copySourceSession.description && (
                            <p className="text-sm text-base-content/70 font-grotesk">
                              {copySourceSession.description}
                            </p>
                          )}
                        </div>

                        <div className="rounded-md border p-4 border-base-300 bg-base-200/40 max-h-80 overflow-y-auto">
                          <SessionActivitiesTable
                            measurements={
                              copySourceSession.ActivityMeasurementList || []
                            }
                            getActivityName={getCopyPreviewActivityName}
                          />
                        </div>

                        <div className="rounded-md border border-base-300 bg-base-200/30 p-4 space-y-2">
                          <div className="text-xs font-semibold text-base-content/80 uppercase tracking-wide">
                            Import process
                          </div>
                          <div className="text-sm text-base-content/80 font-grotesk flex items-center gap-2">
                            <span
                              className={`inline-flex w-5 h-5 items-center justify-center rounded-full ${
                                copyStep === "creating_activities" ||
                                copyStep === "creating_template" ||
                                copyStep === "done"
                                  ? "bg-green-500 text-white"
                                  : "bg-base-300 text-base-content/60"
                              }`}
                            >
                              {copyStep === "creating_template" ||
                              copyStep === "done" ? (
                                <svg
                                  className="w-3 h-3"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              ) : copyStep === "creating_activities" ? (
                                <span className="loading loading-spinner loading-xs"></span>
                              ) : (
                                "1"
                              )}
                            </span>
                            <span>Add/create required exercises</span>
                          </div>

                          <div className="text-sm text-base-content/80 font-grotesk flex items-center gap-2">
                            <span
                              className={`inline-flex w-5 h-5 items-center justify-center rounded-full ${
                                copyStep === "creating_template" ||
                                copyStep === "done"
                                  ? "bg-green-500 text-white"
                                  : "bg-base-300 text-base-content/60"
                              }`}
                            >
                              {copyStep === "done" ? (
                                <svg
                                  className="w-3 h-3"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              ) : copyStep === "creating_template" ? (
                                <span className="loading loading-spinner loading-xs"></span>
                              ) : (
                                "2"
                              )}
                            </span>
                            <span>Create session template</span>
                          </div>

                          <div className="text-sm text-base-content/80 font-grotesk flex items-center gap-2">
                            <span
                              className={`inline-flex w-5 h-5 items-center justify-center rounded-full ${
                                copyStep === "done"
                                  ? "bg-green-500 text-white"
                                  : "bg-base-300 text-base-content/60"
                              }`}
                            >
                              {copyStep === "done" ? (
                                <svg
                                  className="w-3 h-3"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              ) : (
                                "3"
                              )}
                            </span>
                            <span>
                              Done
                              {copyCreatedTemplateName
                                ? `: ${copyCreatedTemplateName}`
                                : ""}
                            </span>
                          </div>

                          {copyActionMessage && (
                            <div className="text-xs text-base-content/60 pt-1 font-grotesk">
                              {copyActionMessage}
                            </div>
                          )}
                        </div>
                      </>
                    ) : null}
                  </div>

                  <div className="px-8 py-4">
                    {!canCreateSession && copyStep !== "done" ? (
                      <div className="text-center py-2 pb-8">
                        <p className="text-sm text-base-content/70 font-grotesk">
                          Saving session templates is part of Plus.
                        </p>
                        <p className="text-xs text-base-content/40 mt-1 font-grotesk">
                          Upgrade to Plus to save and reuse sessions.
                        </p>
                      </div>
                    ) : (
                      <button
                        onClick={handleAddCopiedSession}
                        disabled={
                          copySessionLoading ||
                          !copySourceSession ||
                          copyInProgress ||
                          copyStep === "done"
                        }
                        className="w-full py-3 text-sm font-bold text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:bg-primary/30 disabled:text-white/50 disabled:cursor-not-allowed font-grotesk"
                      >
                        {copyInProgress
                          ? "Adding..."
                          : copyStep === "done"
                            ? "Added"
                            : "Add this session"}
                      </button>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Help Dialog */}
      {showHelpDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50  backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-base-100 px-8 py-8 pb-0 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-base-content font-heading">
                Library
              </h2>
              <button
                onClick={() => setShowHelpDialog(false)}
                className="text-base-content/50 hover:text-base-content/70 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-8 pt-4 space-y-3">
              {/* Overview */}
              <div>
                <p className="text-base-content/80 text-xl">
                  Sessions are groups of activities. They can be assigned
                  Session Types & Periodisation phases to help you organise your
                  training.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <BottomNavBar />
    </div>
  );
};

export default Sessions;
